<template>
  <div
    :class="{
      'block-probate-quiz-tool container': true,
      'is-quiz-open': isQuizOpen,
      [`is-colour-${section.colour.value}`]: true,
    }"
  >
    <div class="block-probate-quiz-tool__wrapper">
      <div class="block-probate-quiz-tool__intro">
        <div class="columns is-variable is-centered">
          <div class="column is-7-desktop">
            <bard v-if="isQuizOpen" :content="quizHeader" as-content />
            <bard v-else :content="section.intro.text" as-content />
          </div>
        </div>
      </div>

      <details ref="detailsRef" class="block-probate-quiz-tool__details">
        <summary class="is-hidden">
          <span>{{ section.intro.button_label || 'Open' }}</span>
          <span>Close</span>
        </summary>

        <div
          :class="{
            'block-probate-quiz-tool__quiz': true,
            'is-finished': isFinished,
            'show-results': showResults,
          }"
        >
          <div class="block-probate-quiz-tool__quiz-inner">
            <div class="block-probate-quiz-tool__quiz-content">
              <template v-if="showResults">
                <template v-for="(question, index) in questions" :key="`option-results-${index}`">
                  <template v-for="(details, detailsIndex) in question.range_details" :key="`option-results-details-${detailsIndex}`">
                    <probate-quiz-tool-details :on-show="() => showRangeResultsIndex === detailsIndex" :details="details.details" />
                  </template>
                </template>
                <probate-quiz-tool-progress-bar />
                <probate-quiz-tool-back :on-back="handleResultsBack" />
              </template>
              <template v-else>
                <div class="block-probate-quiz-tool__quiz-questions">
                  <probate-quiz-tool-question
                    v-for="(question, index) in questions"
                    :key="`question-${index}`"
                    :is-active="currentQuestionIndex === index"
                    :question="question"
                  />
                </div>

                <div class="block-probate-quiz-tool__quiz-bottom">
                  <probate-quiz-tool-progress-bar />
                  <probate-quiz-tool-back v-if="currentQuestionIndex !== 0 || answerId" />
                </div>
              </template>
            </div>

            <div class="block-probate-quiz-tool__quiz-footer columns is-centered">
              <bard :content="quizFooter" as-content class="column is-8-desktop" />
            </div>
          </div>
        </div>
      </details>

      <div class="block-probate-quiz-tool__button-wrapper columns is-centered">
        <button class="block-probate-quiz-tool__button" @click="handleClick">
          <span>{{ section.intro.button_label || 'Open' }}</span>
          <span>Close</span>

          <pt-icon-chevron-right />
        </button>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import type { SectionProps } from '~/types/components';
import { useSectionProps } from '~/composables/blocks';
import ProbateQuizToolBack from '~/components/global/components/probate-quiz-tool/ProbateQuizToolBack.vue';
import ProbateQuizToolQuestion from '~/components/global/components/probate-quiz-tool/ProbateQuizToolQuestion.vue';
import ProbateQuizToolProgressBar from '~/components/global/components/probate-quiz-tool/ProbateQuizToolProgressBar.vue';
import PtIconChevronRight from '~/components/global/icons/PtIconChevronRight.vue';
import { provideProbateQuizTool } from '~/composables/probate-quiz-tool';

const props: SectionProps = defineProps(useSectionProps());
const section = props.section;

const {
  header: quizHeader,
  footer: quizFooter,
  questions,
  currentQuestionIndex,
  isFinished,
  showResults,
  answerId,
  showRangeResultsIndex,
  isQuizOpen,
  setData,
  clear,
  setShowResults,
  setQuizOpen,
} = provideProbateQuizTool();

const detailsRef = ref<HTMLDetailsElement | null>(null);

onMounted(() => {
  const initialOpen = props.section.quiz_initial_state?.value === 'open';

  setData({
    header: props.section.quiz.header,
    footer: props.section.quiz.footer,
    isQuizOpen: initialOpen,
    questions: props.section.questions,
    currentQuestionIndex: 0,
  });

  if (detailsRef.value) {
    detailsRef.value.open = initialOpen;
  }
});

const handleResultsBack = () => {
  setShowResults(false);

  const contentRef = document.querySelector('.block-probate-quiz-tool__quiz-header');
  if (contentRef) {
    contentRef.scrollIntoView({ behavior: 'smooth' });
  }
};

const handleClick = () => {
  if (!detailsRef.value) {
    return;
  }

  detailsRef.value.open = !detailsRef.value.open;

  setQuizOpen(detailsRef.value.open);

  if (!detailsRef.value.open) {
    clear();
  }
};
</script>
